import { doc, getDoc, updateDoc, documentId, getDocs, query, where, orderBy } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { permissionsDB, updateRecord, userCollectionsDB } from "../../libs/firebase";
import { generateID } from "ui";
import { CollectionInterface } from "ts-interfaces";
import { firebaseHelper } from "ui";
import { ProjectSchemeInterface, Permissions } from "ts-interfaces";
import { hasAccessToResource } from "ui/utils/usePermissions";

export async function duplicateCollectionScheme(collection: CollectionInterface): Promise<CollectionInterface> {
    const auth = getAuth();
      const user = auth.currentUser;
      if (!user) throw new Error("User not authenticated");
    
      const duplicatedCollection = {
        ...collection,
        name: `Copy of ${collection.name}`,
        id: generateID(),
        createdAt: Date.now(),
        updatedAt: Date.now(),
        // Reset these fields for the duplicate
        associatedBoardUrl: "",
        associatedProjectId: "",
        writeAccess: [], // Empty array instead of inheriting from original
        uid: user.uid, // Set to the current user creating the duplicate
      };
    
      try {
        await updateRecord(duplicatedCollection.id, "usersMyCollection", duplicatedCollection);
      } catch (error) {
        console.error("Error updating project:", error); // Log the error
        throw new Error("Failed to update project"); // Optionally rethrow the error with a custom message
      }
      return duplicatedCollection;
}

export async function getSharedCollections(
  permissions: Permissions | null
): Promise<CollectionInterface[]> {
  if (!userCollectionsDB || !permissions) return [];
  
  try {
    // Get shared collection IDs from permissions
    const sharedCollectionIds = [...new Set([
      ...(permissions.collectionRead || []), 
      ...(permissions.collectionWrite || [])
    ])];
    
    if (sharedCollectionIds.length === 0) {
      return [];
    }
    
    const allSharedCollections: CollectionInterface[] = [];
    
    // Fetch shared collections in batches of 10 (Firestore limit)
    for (let i = 0; i < sharedCollectionIds.length; i += 10) {
      const batch = sharedCollectionIds.slice(i, i + 10);
      const batchQuery = query(
        userCollectionsDB,
        where(documentId(), "in", batch)
      );
      
      const batchSnapshot = await getDocs(batchQuery);
      const batchCollections = batchSnapshot.docs.map(doc => doc.data() as CollectionInterface);
      allSharedCollections.push(...batchCollections);
    }
    
    return allSharedCollections;
  } catch (error) {
    console.error('Error fetching shared collections:', error);
    return [];
  }
}

export async function associateCollectionWithProject(
  collectionId: string, 
  projectId: string | null, 
  user_id: string, 
  permissions: Permissions | null
): Promise<void> {
  const docSnap = await getCollectionIfAccessible(collectionId, user_id, permissions);
  if (!docSnap) {
    throw new Error("No access to the collection");
  }

  const currentCollectionData = docSnap.data() as CollectionInterface;
  const currentWriteAccess = currentCollectionData.writeAccess || [];
  
  let updatedWriteAccess = [...currentWriteAccess];

  // If projectId is provided, verify user has access to the project
  if (projectId) {
    const projectDoc = await getDoc(doc(firebaseHelper.db, "projectsScheme", projectId));
    if (!projectDoc.exists()) {
      throw new Error("Project not found");
    }
    
    const projectData = projectDoc.data() as ProjectSchemeInterface;
    const hasProjectAccess = hasAccessToResource(permissions, projectId, 'write', 'project', projectData.owner || '', user_id);

    if (!hasProjectAccess) {
      throw new Error("No access to the project");
    }

    // Get ALL users who have write access to this project
    const projectUsers = await getAllUsersWithProjectWriteAccess(firebaseHelper.db, projectId);
    
    // Add all project users to writeAccess (avoiding duplicates)
    const writeAccessSet = new Set(updatedWriteAccess);
    projectUsers.forEach(userId => writeAccessSet.add(userId));
    updatedWriteAccess = Array.from(writeAccessSet);
  }
  // If removing from project (projectId is null), remove project users but keep direct board users
  else if (currentCollectionData.associatedProjectId) {
    // Get users who had access via the old project
    const oldProjectUsers = await getAllUsersWithProjectWriteAccess(
      firebaseHelper.db, 
      currentCollectionData.associatedProjectId
    );
    
    // Get users who have direct board access
    const directBoardUsers = await getUsersWithDirectCollectionAccess(firebaseHelper.db, collectionId);
    const directBoardUsersSet = new Set(directBoardUsers);
    
    // Remove old project users who don't have direct board access
    updatedWriteAccess = updatedWriteAccess.filter(userId => 
      !oldProjectUsers.includes(userId) || directBoardUsersSet.has(userId)
    );
  }

  // Update the collection
  await updateDoc(docSnap.ref, {
    associatedProjectId: projectId,
    writeAccess: updatedWriteAccess,
    updatedAt: Date.now()
  });
}

// Bulk association function for multiple collections
export async function associateMultipleCollectionsWithProject(
  collectionsToAdd: string[], 
  collectionsToRemove: string[], 
  projectId: string | null, 
  user_id: string, 
  permissions: Permissions | null
): Promise<void> {
  // Validate project access first if projectId is provided
  if (projectId) {
    const projectDoc = await getDoc(doc(firebaseHelper.db, "projectsScheme", projectId));
    if (!projectDoc.exists()) {
      throw new Error("Project not found");
    }
    
    const projectData = projectDoc.data() as ProjectSchemeInterface;
    const hasProjectAccess = hasAccessToResource(permissions, projectId, 'write', 'project', projectData.owner || '', user_id);

    if (!hasProjectAccess) {
      throw new Error("No access to the project");
    }
  }

  // Process all collections in parallel
  const allCollectionIds = [...collectionsToAdd, ...collectionsToRemove];
  const collectionPromises = allCollectionIds.map(async (collectionId) => {
    try {
      if (collectionsToAdd.includes(collectionId)) {
        // Associate with project
        await associateCollectionWithProject(collectionId, projectId, user_id, permissions);
      } else {
        // Remove from project (set to null)
        await associateCollectionWithProject(collectionId, null, user_id, permissions);
      }
    } catch (error) {
      console.error(`Error updating collection ${collectionId}:`, error);
      throw error;
    }
  });

  await Promise.all(collectionPromises);
}


// export async function getAssociatedCollections(projectId: string): Promise<CollectionInterface[]> {
//   const auth = getAuth();
//   const user = auth.currentUser;
//   if (!user) throw new Error("User not authenticated");

//   if (!userCollectionsDB) return [];

//   const q = query(
//     userCollectionsDB,
//     where('associatedProjectId', '==', projectId),
//     orderBy('updatedAt', 'desc'),
//     limit(3),
//   );
//   const collections = await getDocs(q);
//   return collections.docs.map(doc => (doc.data()  as CollectionInterface));
// }

// Helper function to get collection if user has access to it
async function getCollectionIfAccessible(collectionId: string, user_id: string, permissions: Permissions | null) {
  if (!user_id) throw new Error("User not authenticated");
  if (!collectionId) throw new Error("Invalid collection ID");

  //we need to assume that a usersMyCollection could be protected by permissions.  May throw an error
  try {
    const docSnap = await getDoc(doc(firebaseHelper.db, "usersMyCollection", collectionId));
    if (!docSnap.exists()) return null;
    const collectionData = docSnap.data() as CollectionInterface;
    const hasSharedAccess = hasAccessToResource(permissions, collectionId, 'read', 'collection', collectionData.uid || '', user_id);
    if (!hasSharedAccess) {
      throw new Error("You don't have permission to access this collection");
    }
    return docSnap;
  }
  catch (error) {
     throw new Error("You don't have permission to access this collection");
  }
  // const isOwner = collectionData.uid === user_id || collectionData.owner === user_id;
  // const hasSharedAccess = permissions?.collectionWrite?.includes(collectionId) || 
  //                        permissions?.collectionRead?.includes(collectionId);
  
  // if (!isOwner && !hasSharedAccess) {
  //   throw new Error("You don't have permission to access this collection");
  // }
}

export async function getCollectionsForProject(
  projectId: string, 
  user_id: string, 
  permissions: Permissions | null
): Promise<CollectionInterface[]> {
  if (!userCollectionsDB || !projectId) return [];
  
  try {
    // Query collections where associatedProjectId equals the project ID
    const q = query(
      userCollectionsDB,
      where("associatedProjectId", "==", projectId),
      orderBy("updatedAt", "desc")
    );
    
    const snapshot = await getDocs(q);
    const collections = snapshot.docs.map(doc => doc.data() as CollectionInterface);
    return collections;
  } catch (error) {
    console.error('Error fetching collections for project:', error);
    return [];
  }
}

export async function getCollectionsForProjects(
  projectIds: string[], 
  user_id: string, 
  permissions: Permissions | null
): Promise<Map<string, CollectionInterface[]>> {
  if (!userCollectionsDB || projectIds.length === 0) return new Map();
  
  try {
    const collectionsByProject = new Map<string, CollectionInterface[]>();
    
    // Initialize map with empty arrays
    projectIds.forEach(id => collectionsByProject.set(id, []));

    // Fetch collections in batches
    for (let i = 0; i < projectIds.length; i += 10) {
      const batch = projectIds.slice(i, i + 10);
      const q = query(
        userCollectionsDB,
        where("associatedProjectId", "in", batch),
        orderBy("updatedAt", "desc")
      );
      
      const snapshot = await getDocs(q);
      
      snapshot.docs.forEach(doc => {
        const collection = doc.data() as CollectionInterface;
        if (collection.associatedProjectId) {
          const projectCollections = collectionsByProject.get(collection.associatedProjectId) || [];
          projectCollections.push(collection);
          collectionsByProject.set(collection.associatedProjectId, projectCollections);
        }
      });
    }
    
    return collectionsByProject;
  } catch (error) {
    console.error('Error fetching collections for projects:', error);
    return new Map();
  }
}

// Helper function to get ALL users with write access to a project
async function getAllUsersWithProjectWriteAccess(db: any, projectId: string): Promise<string[]> {
  if(!permissionsDB) return [];
  try {
    const usersQuery = query(permissionsDB,
      where('projectWrite', 'array-contains', projectId)
    );
    const usersSnapshot = await getDocs(usersQuery);
    return usersSnapshot.docs.map(doc => doc.id);
  } catch (error) {
    console.error('Error getting users with project write access:', error);
    return [];
  }
}

// Helper function to get users with direct board access
async function getUsersWithDirectCollectionAccess(db: any, collectionId: string): Promise<string[]> {
  if(!permissionsDB) return [];
  try {
    const usersQuery = query(
      permissionsDB,
      where('collectionWrite', 'array-contains', collectionId)
    );
    const usersSnapshot = await getDocs(usersQuery);
    return usersSnapshot.docs.map(doc => doc.id);
  } catch (error) {
    console.error('Error getting users with direct board access:', error);
    return [];
  }
}